import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { Loader2, User, Phone, Mail, Calendar, Scale, Calculator, ShieldCheck, Banknote, MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';

const SERVICE_META = {
  legal: { label: 'Legal Advice', icon: Scale, color: 'bg-purple-100 text-purple-700' },
  taxation: { label: 'Taxation', icon: Calculator, color: 'bg-blue-100 text-blue-700' },
  home_inspection: { label: 'Home Inspection', icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-700' },
  home_loan: { label: 'Home Loan', icon: Banknote, color: 'bg-amber-100 text-amber-700' },
};

const STATUS_META = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const AdminServiceRequestsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (filter !== 'all') params.service = filter;
    adminApi.get('/service-requests', { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await adminApi.patch(`/service-requests/${id}`, null, { params: { status } });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast({ title: 'Status updated', description: STATUS_META[status].label });
    } catch (err) {
      toast({ title: 'Failed to update', description: err.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <AdminLayout title="Service Requests">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-gray-600"><span className="font-bold text-gray-900">{total}</span> total</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Filter by service:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-52 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {Object.entries(SERVICE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-sm text-gray-500">
          <User className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          No service requests yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((r) => {
            const meta = SERVICE_META[r.service] || { label: r.service, icon: User, color: 'bg-gray-100 text-gray-700' };
            const Icon = meta.icon;
            const status = STATUS_META[r.status] || STATUS_META.new;
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${meta.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 truncate">{r.name}</div>
                      <div className="text-xs text-gray-500">{meta.label}</div>
                    </div>
                  </div>
                  <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${status.color}`}>{status.label}</span>
                </div>

                <div className="mt-4 space-y-1.5 text-sm text-gray-700">
                  <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" /> <a href={`tel:${r.phone}`} className="hover:text-[#9a1b2f]">{r.phone}</a></div>
                  {r.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /> <a href={`mailto:${r.email}`} className="hover:text-[#9a1b2f]">{r.email}</a></div>}
                  {r.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {r.city}</div>}
                  <div className="flex items-center gap-2 text-[11px] text-gray-500"><Calendar className="w-3 h-3" /> {formatDate(r.created_at)}</div>
                  {r.message && <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 mt-2">“{r.message}”</div>}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Set status:</span>
                  <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminServiceRequestsPage;
